import { DOCUMENT } from '@angular/common';
import { ApplicationRef, DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Contract } from '@app/features/contracts/contract.model';
import { DEMO_TOUR_ID } from '@app/features/tours/demo/demo-mode';
import { LayoutService } from '@app/nav/layout/layout.service';
import { KeycloakTokenRefresher } from '@app/shared/auth/keycloak/keycloak-token-refresher.service';
import { RolesRevision } from '@app/shared/auth/roles-revision';
import Keycloak from 'keycloak-js';
import { combineLatest, filter, map, Observable, startWith, Subject } from 'rxjs';
import packageJson from '../../../../package.json';
import { EMBEDDING } from './embedding.token';
import {
  authMessage,
  menuMessage,
  readShellOrder,
  readyMessage,
  Rubric,
  ShellFolder,
  ShellOrder,
} from './shell-protocol';

/** Les rubriques qui ouvrent, dans la page, un menu de la barre du haut. */
export type MenuRubric = 'societes' | 'grilles' | 'anomalies';

/**
 * Les rubriques de CDT au menu de la coque : celles de sa barre du haut, sans regroupement ni
 * compteur (cadrage 2026-031). Le menu « Parcours » n'en est pas (RG-parcours-catalogue,
 * décision 2026-032/11). **Liste à faire valider par le client** — question ouverte du cadrage ;
 * « Réglages des notifications » s'y ajoute parce que la barre du haut l'a reçu depuis.
 */
const RUBRICS: (Rubric & { menu?: MenuRubric; url?: string; withApi?: boolean })[] = [
  { id: 'contrats', lb: 'Contrats', url: '/contrats' },
  { id: 'societes', lb: 'Sociétés', menu: 'societes' },
  { id: 'grilles', lb: 'Grilles de salaires', menu: 'grilles' },
  // Les anomalies viennent de l'API : un onglet de démonstration n'en a pas le menu.
  { id: 'anomalies', lb: 'Anomalies', menu: 'anomalies', withApi: true },
  { id: 'notifications', lb: 'Réglages des notifications', url: '/reglages/notifications' },
];

/**
 * Le dialogue de CDT avec l'Espace Pilotage qui l'encadre (contrat des logiciels, § 4). Sans
 * coque, il ne fait rien : l'application ouverte seule n'envoie ni n'accepte aucun message.
 */
@Injectable({ providedIn: 'root' })
export class ShellService {
  private readonly embedding = inject(EMBEDDING);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);
  private readonly keycloak = inject(Keycloak);
  private readonly rolesRevision = inject(RolesRevision);
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly demo = !!inject(DEMO_TOUR_ID, { optional: true });

  private readonly rubrics = RUBRICS.filter((rubric) => !(this.demo && rubric.withApi));
  private readonly menuRequestsSubject = new Subject<MenuRubric>();
  private started = false;
  private lastMenu?: { vue: string; ctx: ShellFolder | null };

  /** Une rubrique de la coque demande d'ouvrir, dans la page, un menu de la barre du haut. */
  readonly menuRequests: Observable<MenuRubric> = this.menuRequestsSubject.asObservable();

  get embedded(): boolean {
    return this.embedding !== null;
  }

  /** Écoute la coque, lui dit que CDT est prêt, et publie son menu à chaque changement. */
  start(): void {
    const embedding = this.embedding;
    if (!embedding || this.started) {
      return;
    }
    this.started = true;

    const listener = (event: MessageEvent) => {
      const order = readShellOrder(embedding.coque, embedding.parent, event);
      if (order) {
        this.obey(order);
      }
    };
    embedding.self.addEventListener('message', listener);
    this.destroyRef.onDestroy(() => embedding.self.removeEventListener('message', listener));

    this.send(readyMessage());

    const vue$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
      map(rubricOf),
    );
    const ctx$ = this.layoutService.contract$.pipe(map(folderOf));
    const subscription = combineLatest([vue$, ctx$]).subscribe(([vue, ctx]) =>
      this.publishMenu(vue, ctx),
    );
    // Ouverte seule, c'est le menu utilisateur qui lance le renouvellement périodique du jeton
    // (par LogoutService) ; embarquée, il n'y a pas de menu utilisateur : il se lance ici.
    subscription.add(
      this.injector.get(KeycloakTokenRefresher).failures.subscribe(() => this.sessionLost()),
    );
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  /**
   * Le jeton manque, ou ne se renouvelle plus : la coque renouvelle sa propre session et
   * recharge le cadre (§ 4.4). Sans coque, rien.
   */
  sessionLost(): void {
    this.send(authMessage());
  }

  private obey(order: ShellOrder): void {
    if ('go' in order) {
      this.go(order.go);
    } else if ('toggle' in order) {
      // CDT n'a pas de groupe à plier : la coque redessine le menu tel qu'il est.
      this.republishMenu();
    } else if ('theme' in order) {
      // Appliqué tant que CDT est dans la coque, jamais retenu comme choix de l'utilisateur.
      this.document.documentElement.dataset['theme'] = order.theme;
    } else {
      this.renewToken();
    }
  }

  private go(id: string): void {
    const rubric = this.rubrics.find((candidate) => candidate.id === id);
    if (rubric?.url) {
      void this.router.navigateByUrl(rubric.url);
    } else if (rubric?.menu) {
      this.menuRequestsSubject.next(rubric.menu);
    } else {
      // Une rubrique inconnue, ou que cet onglet n'a pas : ignorée, et le menu republié (§ 4.5).
      this.republishMenu();
    }
  }

  /** Les droits ou le nom ont changé dans la coque : relire le jeton, et redessiner (§ 4.7). */
  private renewToken(): void {
    this.keycloak.updateToken(-1).then(
      () => {
        this.rolesRevision.bump();
        this.appRef.tick();
      },
      // Un compte désactivé ne renouvelle plus son jeton.
      () => this.sessionLost(),
    );
  }

  private publishMenu(vue: string, ctx: ShellFolder | null): void {
    this.lastMenu = { vue, ctx };
    this.send(menuMessage({ items: this.rubrics, vue, ver: `v${packageJson.version}`, ctx }));
  }

  private republishMenu(): void {
    if (this.lastMenu) {
      this.publishMenu(this.lastMenu.vue, this.lastMenu.ctx);
    }
  }

  private send(message: object): void {
    this.embedding?.parent.postMessage(message, this.embedding.coque);
  }
}

/** La rubrique que montre une adresse : un contrat ouvert reste sous « Contrats », en dossier. */
function rubricOf(url: string): string {
  return url.startsWith('/reglages/notifications') ? 'notifications' : 'contrats';
}

/** Le contrat ouvert, s'il est enregistré : sa clé est tirée de son identifiant (cadrage 2026-031). */
function folderOf(contract: Contract | undefined): ShellFolder | null {
  if (!contract?.id) {
    return null;
  }
  const employee = [contract.employee?.firstname, contract.employee?.lastname]
    .filter(Boolean)
    .join(' ');
  const company = contract.company?.name;
  return {
    key: `contrat:${contract.id}`,
    lb: [employee ? `Contrat de ${employee}` : 'Contrat', company].filter(Boolean).join(' — '),
  };
}
